import { useMemo } from 'react';

import {
  Configuration,
  NextPortApi,
  type NextPortArrival,
  type NextportArrivalsClusterGet200Response,
} from '@ais-ui/api-spec/shiptracker';
import { useSession } from '@ais-ui/websockets';
import { UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials, parseDate } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

import { WithDates } from './types';

export type NextPortArrivalWithDate = WithDates<NextPortArrival, 'eta' | 'ata'>;

type NextportArrivalsClusterGetDatesConverted200Response = Omit<
  NextportArrivalsClusterGet200Response,
  'records'
> & {
  records: Array<NextPortArrivalWithDate>;
};

/**
 * A next-port record that is a genuine prediction: it has both an ETA and a
 * probability. Produced by {@link isNextPortPrediction}, so consumers that
 * filter on it get non-optional `eta`/`probability` without re-checking.
 */
export type NextPortPrediction = NextPortArrivalWithDate & {
  eta: Date;
  probability: number;
};

/**
 * A next-port record describing where the vessel is *now* rather than where it
 * is going: it carries an `ata` and no prediction fields. Produced by
 * {@link isCurrentPortCall}.
 */
export type NextPortCurrentPortCall = NextPortArrivalWithDate & {
  ata: Date;
};

/**
 * True for a predicted destination with both an ETA and a probability.
 * The NextPort ship endpoint also returns the vessel's *current* port call
 * (a record carrying `ata` instead of `eta`/`probability`) when it has no
 * prediction to offer, so consumers must filter those out before ranking.
 */
export const isNextPortPrediction = (
  record: NextPortArrivalWithDate,
): record is NextPortPrediction =>
  record.eta != null && record.probability != null;

/** True for a record describing where the vessel is now, not where it is going. */
export const isCurrentPortCall = (
  record: NextPortArrivalWithDate,
): record is NextPortCurrentPortCall =>
  !isNextPortPrediction(record) && record.ata != null;

const parseRecordDates = (
  record: NextPortArrival,
): NextPortArrivalWithDate => ({
  ...record,
  eta: parseDate(record.eta),
  ata: parseDate(record.ata),
});

const useNextportApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;

  const username = credentials?.username || '';
  const password = credentials?.password || '';

  const authHeader = getAuthHeaderFromCredentials(username, password);

  if (hostname.includes('localhost')) {
    basePath = 'https://maritime.gatehouse.dk/api/shiptracker';
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/shiptracker`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const nextportApi = useMemo(
    () => new NextPortApi(configuration),
    [configuration],
  );

  // The wrapper is used to get error messages from the backend, as they are otherwise thrown out
  return apiClientWrapper(nextportApi) as NextPortApi;
};

export const useNextportShipImoGet = (
  imo: string,
  inclAnchorages: boolean,
): UseQueryResult<NextportArrivalsClusterGetDatesConverted200Response> => {
  const nextportApi = useNextportApi();
  return useQuery(
    {
      queryKey: ['nextportImo', imo, inclAnchorages],
      queryFn: async () => {
        const response = await nextportApi.nextportShipImoGet({
          imo: imo,
          inclAnchorages: inclAnchorages || undefined,
        });

        return { ...response, records: response.records.map(parseRecordDates) };
      },
    },
    'nextport:byImo',
  );
};

export const useNextportPortGet = (
  locode: string,
  inclAnchorages: boolean,
): UseQueryResult<NextportArrivalsClusterGetDatesConverted200Response> => {
  const nextportApi = useNextportApi();

  return useQuery(
    {
      queryKey: ['nextportLocode', locode, inclAnchorages],
      queryFn: async () => {
        // Fetch data from the API
        const response = await nextportApi.nextportArrivalsClusterGet({
          cluster: locode,
          inclAnchorages: inclAnchorages || undefined,
        });

        return { ...response, records: response.records.map(parseRecordDates) };
      },
      enabled: !!locode,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Consumers (e.g. PortDetailsPanel) render their own inline error
      // fallback — suppress the global error toast to avoid double-reporting.
      meta: { suppressErrorToast: true },
    },
    'nextport:byLocode',
  );
};
